//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useCallback, useContext, useState } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';

// import Checkbox from '@material-ui/core/Checkbox';
// import FormControlLabel from '@material-ui/core/FormControlLabel';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getCreatableLovSuggestions } from '../../common/lovHelper';
import { ELASTICSEARCH_TYPE_INDUSTRY } from '../../common/elasticSearchHelper';
import { PLACEHOLDER_SEARCH_BY_LOCATION } from '../../common/placeholderHelper';
import { getLocationSuggestions } from '../../common/services/jobCenterServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdAutocompleteLite from '../../common/elemental/AfitdAutocompleteLite';
import AfitdAutocomplete from '../../common/elemental/AfitdAutocomplete';
import AfitdCompanySizeAutocomplete from '../../common/elemental/AfitdCompanySizeAutocomplete';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const filterFieldStyles = { background: '#FFF' };
    return makeStyles(theme => ({
        root: {
            background: '#F0F2F6',
            borderRadius: 10,
            padding: 24,
            paddingBottom: 48,
            // height: '510px',
            marginRight: 24,
            marginBottom: 24,
            // height: '100%',
            [theme.breakpoints.down('md')]: { marginRight: 0, height: '300px' },
            [theme.breakpoints.down('sm')]: { marginRight: 0, height: '475px' },
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
        },
        heading: {
            fontWeight: 'bold',
            fontSize: '18px',
            color: '#111E14',
        },
        clearAllLink: {
            color: '#1D8A8B',
            fontSize: '13px',
            fontWeight: 'bold',
            '&:hover': {
                cursor: 'pointer',
                textDecoration: 'underline',
            },
        },
        activeJobsCheckBox: {
            marginTop: 20,
        },
        fieldLabel: {
            fontWeight: 'bold',
            fontSize: '14px',
            lineHeight: '16px',
            marginTop: '40px',
            [theme.breakpoints.down('sm')]: { marginTop: '16px' },
        },
        headerTabs: {
            background: '#FAFAFC',
        },
        industry: {
            ...filterFieldStyles,
        },
        companySize: {
            ...filterFieldStyles,
        },
        location: {
            ...filterFieldStyles,
        },
        filterContainer: {
            [theme.breakpoints.down('md')]: { display: 'flex', flexDirection: 'column-reverse' },
        },
        filterInputBoxes: {
            [theme.breakpoints.down('md')]: { display: 'flex', justifyContent: 'space-between' },
            [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
        },
        filterInputBox: { [theme.breakpoints.down('md')]: { width: '100%', paddingRight: 16 }, [theme.breakpoints.down('sm')]: { paddingRight: 0 } },
        filterInputBoxLast: { width: '100%' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        esToken,
        setEsToken,
        industry,
        companySize,
        searchLocation,
        handleChangeIndustry,
        handleChangeCompanySize,
        handleChangeLocation,
        industryOptions,
        setIndustryOptions,
        locationOptions,
        setLocationOptions,
        // haveActiveJobs,
        // setHaveActiveJobs,
        handleClearAllFilters,
    } = params;

    const defns = {
        // activeJobsCheckBox: {
        //     control: <Checkbox checked={haveActiveJobs} onClick={evt => setHaveActiveJobs(!haveActiveJobs)} />,
        //     label: 'with Active Jobs',
        // },
        industry: {
            name: 'industry',
            placeholder: 'Ex. Healthcare',
            options: industryOptions,
            value: industry,
            onChange: handleChangeIndustry,
            onSuggestionsFetchRequested: async value => {
                if (value) {
                    const options = await getCreatableLovSuggestions({ type: ELASTICSEARCH_TYPE_INDUSTRY, keywords: value, esToken, setEsToken });
                    setIndustryOptions(options);
                } else setIndustryOptions([]);
            },
        },
        companySize: {
            name: 'companySize',
            value: companySize,
            onChange: handleChangeCompanySize.bind(null, {}),
        },
        location: {
            value: searchLocation,
            onChange: handleChangeLocation,
            placeholder: PLACEHOLDER_SEARCH_BY_LOCATION,
            options: locationOptions,
            getOptionSelected: (option, value) => option?.label === value?.label,
            onSuggestionsFetchRequested: async value => {
                if (value) {
                    const options = await getLocationSuggestions({ keywords: value, esToken, setEsToken });
                    setLocationOptions(options);
                } else setLocationOptions([]);
            },
        },
        clearAllLink: {
            onClick: handleClearAllFilters,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SearchPanel = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles()();

    const appContext = useContext(AppContext);
    const { esToken, setEsToken } = appContext;

    // const userCountry = _get(currentUser, 'country');
    // const userState = _get(currentUser, 'state');
    // const userCity = _get(currentUser, 'city');

    const layoutContext = useContext(LayoutContext);
    const { queryInitState, setQuery, searchLocation, setSearchLocation /* setFilters /*, haveActiveJobs, setHaveActiveJobs, */ /*setSnackbarMessage, setSnackbarErrorMessage*/ } =
        layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { setShowAds } = props;

    // const fetchDefaultSearchLocation = useCallback(
    //     async keywords => {
    //         const options = await getLocationSuggestions({ keywords, esToken, setEsToken });
    //         const firstOption = _get(options, '[0]');
    //         setSearchLocation(firstOption);
    //     },
    //     [setSearchLocation, esToken, setEsToken],
    // );

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [industry, setIndustry] = useState(null);
    const [companySize, setCompanySize] = useState(null);
    const [industryOptions, setIndustryOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    // const [isInitSearchLocation, setIsInitSearchLocation] = useState(true);
    // const [isDirty, setIsDirty] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // initial Location search criteria to user's location, if entered.
    // useEffect(() => {
    //     if (isInitSearchLocation && userCountry && userState && userCity) {
    //         fetchDefaultSearchLocation(`${userCountry}, ${userState}, ${userCity}`);
    //         setIsInitSearchLocation(false);
    //     }
    // }, [isInitSearchLocation, userCountry, userState, userCity, fetchDefaultSearchLocation]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handler
    //---------------------------------------------------------------------
    const handleChangeIndustry = useCallback(
        (name, value) => {
            setIndustry(value);
            setQuery(prev => ({ ...prev, industry: value, location: searchLocation }));
            if (!value) setIndustryOptions([]);
            setShowAds(false);
        },
        [searchLocation, setQuery, setShowAds],
    );

    const handleChangeCompanySize = useCallback(
        (params, evt, option) => {
            const companySizeMinVal = option?.minVal;
            setCompanySize(companySizeMinVal);
            setQuery(prev => ({ ...prev, companySize: companySizeMinVal, location: searchLocation }));
            setShowAds(false);
        },
        [searchLocation, setQuery, setShowAds],
    );

    const handleChangeLocation = useCallback(
        (evt, option = {}) => {
            setSearchLocation(option);
            setQuery(prev => ({ ...prev, location: option }));
            setShowAds(false);
        },
        [setSearchLocation, setQuery, setShowAds],
    );

    const handleClearAllFilters = () => {
        setIndustry(null);
        setCompanySize(null);
        setSearchLocation(null);
        setQuery(prev => ({ ...prev, ...queryInitState }));
        setShowAds(true);
        // setHaveActiveJobs(false);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        esToken,
        setEsToken,
        industry,
        companySize,
        searchLocation,
        handleChangeIndustry,
        handleChangeCompanySize,
        handleChangeLocation,
        industryOptions,
        setIndustryOptions,
        locationOptions,
        setLocationOptions,
        // haveActiveJobs,
        // setHaveActiveJobs,
        handleClearAllFilters,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.header}>
                <div>
                    <h2 {...defns.heading}>Search Filters </h2>
                </div>
                <div>
                    <a {...defns.clearAllLink}>Clear All</a>
                </div>
            </div>
            <div {...defns.filterContainer}>
                {/* <div>
                    <FormControlLabel {...defns.activeJobsCheckBox} />
                </div> */}
                <div {...defns.filterInputBoxes}>
                    <div {...defns.filterInputBox}>
                        <p {...defns.fieldLabel}>Industry:</p>
                        <AfitdAutocompleteLite {...defns.industry} />
                    </div>
                    <div {...defns.filterInputBox}>
                        <p {...defns.fieldLabel}>Company Size:</p>
                        <AfitdCompanySizeAutocomplete {...defns.companySize} />
                    </div>
                    <div {...defns.filterInputBoxLast}>
                        <p {...defns.fieldLabel}>Location:</p>
                        <AfitdAutocomplete {...defns.location} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchPanel;
